import * as THREE from 'three';
import { events } from '../core/events.js';
import { groundRadius } from './placement.js';
import { colliders } from './colliders.js';
import { spawnPickup } from './pickups.js';
import { COMM_PARTS } from '../game/commParts.js';
import { player } from '../player/controller.js';
import { MOVE_SPEED } from '../config.js';

// A low ring of boulders with an entrance gap on one side, a stone
// pedestal at the back holding the Signal Chip, and three bioluminescent
// guardians curled up asleep around it. Moving fast inside wakes them:
// they scatter inside the cave, and the chip is "locked" until all of
// them have settled back down to sleep. Walk slowly → they stay asleep
// → chip is collectable.

const UP_Y = new THREE.Vector3(0, 1, 0);
// Southern-hemisphere latitude −30°, longitude 300°. Sits on the
// 3-POI triangle with the pond and ruins, 120° of longitude apart.
export const CAVE_DIRECTION = new THREE.Vector3(
  Math.cos(-30 * Math.PI / 180) * Math.cos(300 * Math.PI / 180),
  Math.sin(-30 * Math.PI / 180),
  Math.cos(-30 * Math.PI / 180) * Math.sin(300 * Math.PI / 180),
).normalize();

const DISCOVERY_RANGE      = 6.5;
const CAVE_RADIUS          = 3.3;           // radial distance of boulders
const PEDESTAL_ANGLE       = Math.PI;       // opposite the entrance
const PEDESTAL_RADIUS      = 1.8;
// Global wake gate — player distance from the cave centre.
const WAKE_RANGE           = 6.5;
const WAKE_SPEED_THRESHOLD = MOVE_SPEED * 0.45;  // faster than this wakes them
const PANIC_DURATION       = 4.5;           // seconds in the blockade pose
const RETURN_DURATION      = 2.0;           // lerp back to sleep
const SETTLE_DELAY         = 1.5;           // quiet time before re-sleeping
// Intercept wall — where guardians form up to block the archway.
const INTERCEPT_RADIAL     = 2.5;           // cave-local distance from centre
const SLOT_WIDTH           = 1.3;           // lateral spacing in the wall
const LEAP_DURATION        = 0.35;          // sec for wake-leap to the wall
const LEAP_HEIGHT          = 0.55;          // peak hop height mid-leap

let caveGroup    = null;
let chipEntry    = null;
let discovered   = false;
let planetRef    = null;

const guardians  = [];

// Scratch vectors reused across frames to avoid allocating per-creature
// per-frame during the panic animation.
const _playerLocal = new THREE.Vector3();
const _toPlayer    = new THREE.Vector3();
const _invCaveQuat = new THREE.Quaternion();

function _rotation(angle) {
  return { cos: Math.cos(angle), sin: Math.sin(angle) };
}

// Add a live collider for a guardian — called when they wake. Radius is
// generous so three side-by-side guardians form a wall the player can't
// squeeze through; height sits below the cave's ceiling collider.
function _activateGuardianCollider(g) {
  if (g.collider) return;
  g.collider = {
    pos: new THREE.Vector3(),
    up:  CAVE_DIRECTION.clone(),
    r:   0.5,
    h:   0.9,
  };
  g.mesh.getWorldPosition(g.collider.pos);
  colliders.push(g.collider);
}

function _deactivateGuardianCollider(g) {
  if (!g.collider) return;
  const idx = colliders.indexOf(g.collider);
  if (idx >= 0) colliders.splice(idx, 1);
  g.collider = null;
}

function _worldFromLocal(local) {
  const v = local.clone().applyQuaternion(caveGroup.quaternion);
  return v.add(caveGroup.position);
}

function _makeGuardianMesh() {
  // Low dome body with two closed-eye slits. When awake, eye dots appear
  // (they're always in the mesh but we toggle emissiveIntensity).
  const g = new THREE.Group();

  const bodyMat = new THREE.MeshStandardMaterial({
    color: 0x96cfd4, roughness: 0.8, flatShading: true,
    emissive: 0x2a6a80, emissiveIntensity: 0.25,
  });
  const body = new THREE.Mesh(
    new THREE.SphereGeometry(0.32, 18, 14, 0, Math.PI * 2, 0, Math.PI / 2),
    bodyMat,
  );
  body.scale.set(1, 0.6, 1);
  body.castShadow = true;
  g.add(body);

  // Small tuft (spikes) just for silhouette character.
  for (const dz of [-0.08, 0.08]) {
    const spike = new THREE.Mesh(
      new THREE.ConeGeometry(0.04, 0.1, 5),
      new THREE.MeshStandardMaterial({
        color: 0x6aa8b0, roughness: 0.8, flatShading: true,
      }),
    );
    spike.position.set(0, 0.2, dz);
    spike.rotation.z = dz > 0 ? 0.2 : -0.2;
    g.add(spike);
  }

  // Eyes — emissive spheres that only brighten when awake.
  const eyeMat = new THREE.MeshStandardMaterial({
    color: 0xfff4a0, emissive: 0xfff4a0, emissiveIntensity: 0,
  });
  const eyeL = new THREE.Mesh(new THREE.SphereGeometry(0.025, 10, 8), eyeMat);
  const eyeR = new THREE.Mesh(new THREE.SphereGeometry(0.025, 10, 8), eyeMat.clone());
  eyeL.position.set(-0.08, 0.14, 0.26);
  eyeR.position.set( 0.08, 0.14, 0.26);
  g.add(eyeL); g.add(eyeR);

  g.userData = { bodyMat, eyeMatL: eyeL.material, eyeMatR: eyeR.material };
  return g;
}

export function createCave(scene, planet) {
  planetRef = planet;

  caveGroup = new THREE.Group();
  const groundR = groundRadius(planet, CAVE_DIRECTION);
  caveGroup.quaternion.setFromUnitVectors(UP_Y, CAVE_DIRECTION);
  caveGroup.position.copy(CAVE_DIRECTION).multiplyScalar(groundR);
  scene.add(caveGroup);

  // ---- Materials ----
  // Cooler, darker palette than the ruins' weathered grey — the cave
  // reads as damp, sunless, mineral.
  const rockMat = new THREE.MeshStandardMaterial({
    color: 0x353843, roughness: 0.95, flatShading: true,
  });
  const darkRockMat = new THREE.MeshStandardMaterial({
    color: 0x1f2230, roughness: 0.95, flatShading: true,
  });
  const dirtMat = new THREE.MeshStandardMaterial({
    color: 0x2a1e10, roughness: 1, flatShading: true,
  });
  const pedestalMat = new THREE.MeshStandardMaterial({
    color: 0x4a4e58, roughness: 0.9, flatShading: true,
  });
  const mossMat = new THREE.MeshStandardMaterial({
    color: 0x3a5e4a, roughness: 0.9, flatShading: true,
    emissive: 0x1a2a22, emissiveIntensity: 0.3,
  });
  const glowPodMat = new THREE.MeshStandardMaterial({
    color: 0x8ce6d4, roughness: 0.6,
    emissive: 0x4ce0c4, emissiveIntensity: 1.2,
  });

  // ---- Dirt floor inside the ring ----
  const floor = new THREE.Mesh(
    new THREE.CircleGeometry(3.2, 48), dirtMat,
  );
  floor.rotation.x = -Math.PI / 2;
  floor.position.y = -0.02;
  floor.receiveShadow = true;
  caveGroup.add(floor);

  // ---- Boulder ring — 7 large rocks around the perimeter, entrance at
  //      angle 0 (90° gap between the two adjacent rocks). ----
  const BOULDER_ANGLES = [];
  for (let i = 1; i <= 7; i++) BOULDER_ANGLES.push((i * Math.PI) / 4);

  for (const ba of BOULDER_ANGLES) {
    const { cos, sin } = _rotation(ba);
    const baseR = CAVE_RADIUS;
    const boulder = new THREE.Mesh(
      new THREE.DodecahedronGeometry(1.05, 0), rockMat,
    );
    boulder.position.set(cos * baseR, 0.85, sin * baseR);
    boulder.scale.set(
      1.1 + Math.random() * 0.2,
      1.55 + Math.random() * 0.2,
      1.1 + Math.random() * 0.2,
    );
    boulder.rotation.y = Math.random() * Math.PI * 2;
    boulder.rotation.z = (Math.random() - 0.5) * 0.25;
    boulder.castShadow = true;
    boulder.receiveShadow = true;
    caveGroup.add(boulder);

    // Collider — slightly wider than the visual so adjacent boulders
    // overlap and no player-sized gap remains between them. Tall enough
    // to block any jump.
    const localOff = new THREE.Vector3(cos * baseR, 0, sin * baseR)
      .applyQuaternion(caveGroup.quaternion);
    colliders.push({
      pos: caveGroup.position.clone().add(localOff),
      up:  CAVE_DIRECTION.clone(),
      r:   1.2,
      h:   2.8,
    });

    // Occasional moss cluster on the boulder's face
    if (Math.random() < 0.6) {
      const moss = new THREE.Mesh(
        new THREE.SphereGeometry(0.18, 10, 8), mossMat,
      );
      moss.position.set(
        cos * (baseR - 0.65),
        0.75 + Math.random() * 0.4,
        sin * (baseR - 0.65),
      );
      moss.scale.set(1.4, 0.4, 1.4);
      caveGroup.add(moss);
    }
  }

  // ---- Fill boulders between the main ones so there are no line-of-
  //      sight gaps through the wall. Sit at the same radial distance,
  //      slightly offset in height so they read as a natural crag. ----
  const FILL_ANGLES = [
    3 * Math.PI / 8, 5 * Math.PI / 8, 7 * Math.PI / 8,
    9 * Math.PI / 8, 11 * Math.PI / 8, 13 * Math.PI / 8,
  ];
  for (const fa of FILL_ANGLES) {
    const { cos, sin } = _rotation(fa);
    const fill = new THREE.Mesh(
      new THREE.DodecahedronGeometry(0.9, 0), rockMat,
    );
    fill.position.set(cos * CAVE_RADIUS, 0.7, sin * CAVE_RADIUS);
    fill.scale.set(
      1.0 + Math.random() * 0.25,
      1.35 + Math.random() * 0.25,
      1.0 + Math.random() * 0.25,
    );
    fill.rotation.y = Math.random() * Math.PI * 2;
    fill.rotation.z = (Math.random() - 0.5) * 0.2;
    fill.castShadow = true;
    fill.receiveShadow = true;
    caveGroup.add(fill);
  }

  // ---- Upper-row rocks bridging the boulder tops to the roof slabs.
  //      Fills the vertical gap so the sky doesn't peek between the
  //      ring wall and the ceiling. ----
  const UPPER_ANGLES = [
    ...BOULDER_ANGLES,
    ...FILL_ANGLES,
  ];
  for (const ua of UPPER_ANGLES) {
    const { cos, sin } = _rotation(ua);
    const cap = new THREE.Mesh(
      new THREE.DodecahedronGeometry(0.7, 0), darkRockMat,
    );
    cap.position.set(cos * (CAVE_RADIUS - 0.15), 2.0, sin * (CAVE_RADIUS - 0.15));
    cap.scale.set(
      1.0 + Math.random() * 0.2,
      0.9 + Math.random() * 0.2,
      1.0 + Math.random() * 0.2,
    );
    cap.rotation.y = Math.random() * Math.PI * 2;
    cap.castShadow = true;
    caveGroup.add(cap);
  }

  // ---- Entrance archway — two lean pillars + a lintel slab ----
  // Positioned at angle 0 (entrance gap). The arch visually frames the
  // way in so it reads as the mouth of a cave, not a gap in a rock
  // garden.
  const archGap = 1.3;
  for (const side of [-1, 1]) {
    const pillar = new THREE.Mesh(
      new THREE.DodecahedronGeometry(0.45, 0), darkRockMat,
    );
    pillar.position.set(CAVE_RADIUS - 0.2, 0.95, side * archGap);
    pillar.scale.set(0.8, 2.1, 0.8);
    pillar.rotation.y = side * 0.3;
    pillar.rotation.z = side * -0.08;
    pillar.castShadow = true;
    caveGroup.add(pillar);
  }
  const lintel = new THREE.Mesh(
    new THREE.DodecahedronGeometry(0.55, 0), darkRockMat,
  );
  lintel.position.set(CAVE_RADIUS - 0.2, 2.15, 0);
  lintel.scale.set(1.0, 0.45, 2.3);
  lintel.rotation.z = 0.06;
  lintel.castShadow = true;
  caveGroup.add(lintel);

  // ---- Roof slabs — nine wide flat rocks leaning inward over the ring,
  //      leaving only the entrance arc uncovered. Creates a solid dome
  //      so no sky peeks through from outside. ----
  const ROOF_ANGLES = [
    Math.PI * 0.3, Math.PI * 0.5, Math.PI * 0.7, Math.PI * 0.9,
    Math.PI * 1.1, Math.PI * 1.3, Math.PI * 1.5, Math.PI * 1.7,
    Math.PI,
  ];
  for (const ra of ROOF_ANGLES) {
    const { cos, sin } = _rotation(ra);
    const slab = new THREE.Mesh(
      new THREE.DodecahedronGeometry(1.1, 0), rockMat,
    );
    slab.position.set(cos * 2.2, 2.5, sin * 2.2);
    slab.scale.set(1.55, 0.5, 1.4);
    slab.rotation.y = ra + Math.PI / 2;
    slab.rotation.x = Math.cos(ra) * 0.22;
    slab.rotation.z = Math.sin(ra) * 0.22;
    slab.castShadow = true;
    slab.receiveShadow = true;
    caveGroup.add(slab);
  }

  // Central capstone to seal the peak of the dome.
  const capstone = new THREE.Mesh(
    new THREE.DodecahedronGeometry(1.1, 0), rockMat,
  );
  capstone.position.set(0, 2.9, 0);
  capstone.scale.set(1.3, 0.55, 1.3);
  capstone.rotation.y = Math.random() * Math.PI;
  capstone.castShadow = true;
  capstone.receiveShadow = true;
  caveGroup.add(capstone);

  // Invisible ceiling collider — covers the cave interior at a height
  // just below where the player's head would clip the roof. The vertical
  // jump check in the controller stops upward motion against this, so
  // jumps inside the cave cap out at a short hop.
  {
    const off = new THREE.Vector3(0, 1.1, 0).applyQuaternion(caveGroup.quaternion);
    colliders.push({
      pos: caveGroup.position.clone().add(off),
      up:  CAVE_DIRECTION.clone(),
      r:   2.6,
      h:   3.0,
    });
  }

  // ---- Stalactites — small pointed cones hanging from the ceiling ----
  for (let i = 0; i < 7; i++) {
    const a = Math.random() * Math.PI * 2;
    const r = 0.6 + Math.random() * 1.6;
    const len = 0.18 + Math.random() * 0.24;
    const tite = new THREE.Mesh(
      new THREE.ConeGeometry(0.1, len, 5), darkRockMat,
    );
    tite.position.set(
      Math.cos(a) * r,
      2.25 - len * 0.5,
      Math.sin(a) * r,
    );
    tite.rotation.x = Math.PI;     // point downward
    tite.rotation.y = Math.random() * Math.PI * 2;
    tite.castShadow = true;
    caveGroup.add(tite);
  }

  // ---- Bioluminescent wall pods — a handful of glowing mushrooms on the
  //      inside of the boulders. The helmet lamp is primary; these just
  //      tint the walls so the interior isn't pitch black. ----
  for (let i = 0; i < 6; i++) {
    const ba = BOULDER_ANGLES[i % BOULDER_ANGLES.length] + (Math.random() - 0.5) * 0.3;
    const cosA = Math.cos(ba);
    const sinA = Math.sin(ba);
    const r = CAVE_RADIUS - 0.75 - Math.random() * 0.3;
    const h = 0.3 + Math.random() * 1.3;

    const cluster = new THREE.Group();
    cluster.position.set(cosA * r, h, sinA * r);
    caveGroup.add(cluster);

    const bulbCount = 2 + Math.floor(Math.random() * 3);
    for (let j = 0; j < bulbCount; j++) {
      const bulb = new THREE.Mesh(
        new THREE.SphereGeometry(0.045 + Math.random() * 0.03, 10, 8),
        glowPodMat,
      );
      bulb.position.set(
        (Math.random() - 0.5) * 0.15,
        (Math.random() - 0.5) * 0.15,
        (Math.random() - 0.5) * 0.15,
      );
      cluster.add(bulb);
    }
  }

  // Rubble + pebbles scattered inside
  for (let i = 0; i < 10; i++) {
    const a = Math.random() * Math.PI * 2;
    const r = 0.8 + Math.random() * 1.8;
    const size = 0.1 + Math.random() * 0.15;
    const rubble = new THREE.Mesh(
      new THREE.DodecahedronGeometry(size), darkRockMat,
    );
    rubble.position.set(
      Math.cos(a) * r, -0.01 + size * 0.25, Math.sin(a) * r,
    );
    rubble.rotation.set(Math.random(), Math.random(), Math.random());
    caveGroup.add(rubble);
  }

  // ---- Pedestal at the back ----
  const pedAngle = PEDESTAL_ANGLE;
  const pedX = Math.cos(pedAngle) * PEDESTAL_RADIUS;
  const pedZ = Math.sin(pedAngle) * PEDESTAL_RADIUS;

  const pedestalBase = new THREE.Mesh(
    new THREE.CylinderGeometry(0.45, 0.55, 0.5, 10), pedestalMat,
  );
  pedestalBase.position.set(pedX, 0.25, pedZ);
  pedestalBase.castShadow = true;
  caveGroup.add(pedestalBase);

  const pedestalTop = new THREE.Mesh(
    new THREE.CylinderGeometry(0.4, 0.45, 0.08, 16), pedestalMat,
  );
  pedestalTop.position.set(pedX, 0.54, pedZ);
  pedestalTop.castShadow = true;
  caveGroup.add(pedestalTop);

  // Short collider for the pedestal so the player can't walk through it.
  {
    const off = new THREE.Vector3(pedX, 0, pedZ).applyQuaternion(caveGroup.quaternion);
    colliders.push({
      pos: caveGroup.position.clone().add(off),
      up:  CAVE_DIRECTION.clone(),
      r:   0.55,
      h:   0.6,
    });
  }

  // ---- Three guardians in the archway area ----
  // Sleeping positions are scattered for a natural look. When woken
  // they leap into a clean wall formation at the intercept line, and
  // leap back to their sleep spots when they settle.
  const slots = [-1, 0, 1];
  const sleepSpots = [
    { x: 2.1,  z: -1.15, rot:  0.35 },   // slightly inside & to the left
    { x: 2.95, z:  0.25, rot: -0.6  },   // at the archway, pointed right
    { x: 2.25, z:  1.0,  rot:  1.1  },   // slightly inside & to the right
  ];
  for (let i = 0; i < 3; i++) {
    const slot  = slots[i];
    const sleep = sleepSpots[i];
    const mesh = _makeGuardianMesh();
    mesh.position.set(sleep.x, 0, sleep.z);
    mesh.rotation.y = sleep.rot;
    mesh.scale.setScalar(0.85 + (i % 2) * 0.1);   // subtle size variation
    caveGroup.add(mesh);

    guardians.push({
      mesh,
      sleepLocal: new THREE.Vector3(sleep.x, 0, sleep.z),
      sleepRot:   sleep.rot,
      wallLocal:  new THREE.Vector3(
        INTERCEPT_RADIAL, 0, SLOT_WIDTH * slot,
      ),
      leapFrom:   new THREE.Vector3(),
      leapTo:     new THREE.Vector3(),
      state: 'sleeping',
      stateTime: 0,
      panicPhase: Math.random() * Math.PI * 2,
      blockadeSlot: slot,
      worldPos: new THREE.Vector3(),
      collider: null,
    });
  }

  // Compute world positions for wake checks.
  caveGroup.updateMatrixWorld(true);
  for (const g of guardians) {
    g.worldPos.copy(g.sleepLocal);
    caveGroup.localToWorld(g.worldPos);
  }

  // ---- Signal Chip pickup on the pedestal, locked by default ----
  const chipPart = COMM_PARTS.find((p) => p.id === 'comm_chip');
  if (chipPart) {
    chipEntry = spawnPickup(scene, planet, {
      ...chipPart, beacon: true, locked: true,
      dir: CAVE_DIRECTION.clone(),
    });
    const topLocal = new THREE.Vector3(pedX, 0.6, pedZ);
    const topWorld = _worldFromLocal(topLocal);
    chipEntry.group.position.copy(topWorld);
    chipEntry.group.quaternion.setFromUnitVectors(UP_Y, CAVE_DIRECTION);
    chipEntry.worldPos.copy(topWorld);
  }

  return { group: caveGroup };
}

export function updateCave(character) {
  if (!caveGroup) return;

  // Discovery beat — fires once when the player first enters range.
  if (!discovered) {
    const d = character.position.distanceTo(caveGroup.position);
    if (d < DISCOVERY_RANGE) {
      discovered = true;
      events.emit('cave:discovered');
    }
  }

  const t = performance.now() / 1000;
  const dt = 1 / 60;          // fixed-ish tick; only used for eased animation

  // Precompute the player's position in cave-local coordinates so each
  // guardian can lunge toward / face it without repeating the transform.
  _invCaveQuat.copy(caveGroup.quaternion).invert();
  _playerLocal.copy(character.position)
    .sub(caveGroup.position)
    .applyQuaternion(_invCaveQuat);
  _playerLocal.y = 0;                    // keep blockade math flat

  // ---- Global wake gate ----
  // Any sleeping guardian wakes as soon as the player approaches the
  // cave at speed. Guardians live at the archway already, so waking
  // simply rouses them in place and activates their colliders — the
  // wall is solid the instant the wake fires.
  const distToCave = character.position.distanceTo(caveGroup.position);
  const playerRunning = Math.abs(player.curMove) > WAKE_SPEED_THRESHOLD;
  if (playerRunning && distToCave < WAKE_RANGE) {
    for (const g of guardians) {
      if (g.state === 'sleeping') {
        g.state = 'waking';
        g.stateTime = LEAP_DURATION;
        g.leapFrom.copy(g.mesh.position);
        g.leapTo.copy(g.wallLocal);
        _activateGuardianCollider(g);
        g.mesh.getWorldPosition(g.collider.pos);
        events.emit('cave:disturbed');
      }
    }
  }

  let anyAwake = false;

  for (const g of guardians) {
    // Update live world position (only really changes during panic/return).
    g.mesh.getWorldPosition(g.worldPos);

    if (g.state === 'sleeping') {
      // Slow breathing pulse — emissive + subtle scale around the low
      // 0.9 sleeping baseline.
      const breathe = 0.25 + 0.15 * (0.5 + 0.5 * Math.sin(t * 1.2 + g.panicPhase));
      g.mesh.userData.bodyMat.emissiveIntensity = breathe;
      g.mesh.userData.eyeMatL.emissiveIntensity = 0;
      g.mesh.userData.eyeMatR.emissiveIntensity = 0;
      g.mesh.scale.setScalar(0.9 + Math.sin(t * 1.2 + g.panicPhase) * 0.025);
    } else if (g.state === 'waking') {
      // Startle-leap from the sleep spot to the wall position. Position
      // interpolates along a parabolic arc so they physically hop into
      // formation; eyes open and body puffs up en route.
      const u = 1 - g.stateTime / LEAP_DURATION;
      g.mesh.position.x = g.leapFrom.x + (g.leapTo.x - g.leapFrom.x) * u;
      g.mesh.position.z = g.leapFrom.z + (g.leapTo.z - g.leapFrom.z) * u;
      g.mesh.position.y = 4 * u * (1 - u) * LEAP_HEIGHT;
      g.mesh.userData.eyeMatL.emissiveIntensity = 1.3 * u;
      g.mesh.userData.eyeMatR.emissiveIntensity = 1.3 * u;
      g.mesh.userData.bodyMat.emissiveIntensity = 0.25 + 0.8 * u;
      g.mesh.scale.setScalar(0.9 + u * 0.3);
      g.stateTime -= dt;
      if (g.stateTime <= 0) {
        g.state = 'panicked';
        g.stateTime = PANIC_DURATION + SETTLE_DELAY;
        g.mesh.position.copy(g.wallLocal);
      }
      anyAwake = true;
    } else if (g.state === 'panicked') {
      // Aggressive sentry — rooted at the wall position, hopping and
      // staring at the player. Position is fixed; only y (hop) and
      // rotation (face player) change.
      g.mesh.position.x = g.wallLocal.x;
      g.mesh.position.z = g.wallLocal.z;

      const hopRate = 8 + g.panicPhase * 0.3;
      const hop = Math.max(0, Math.sin(t * hopRate + g.panicPhase)) * 0.22;
      g.mesh.position.y = hop;

      const faceDx = _playerLocal.x - g.mesh.position.x;
      const faceDz = _playerLocal.z - g.mesh.position.z;
      if (faceDx * faceDx + faceDz * faceDz > 1e-4) {
        g.mesh.rotation.y = Math.atan2(faceDx, faceDz);
      }

      g.mesh.userData.eyeMatL.emissiveIntensity = 1.5 + Math.sin(t * 11) * 0.3;
      g.mesh.userData.eyeMatR.emissiveIntensity = 1.5 + Math.sin(t * 11 + 0.5) * 0.3;
      g.mesh.userData.bodyMat.emissiveIntensity = 1.0;
      g.mesh.scale.setScalar(1.2 + Math.sin(t * 9 + g.panicPhase) * 0.05);

      g.stateTime -= dt;
      if (g.stateTime <= 0) {
        g.state = 'returning';
        g.stateTime = RETURN_DURATION;
      }
      anyAwake = true;
    } else if (g.state === 'returning') {
      // Mirror-leap back from the wall to the sleep spot — same
      // parabolic arc as the wake leap, just in reverse direction.
      const u = 1 - g.stateTime / RETURN_DURATION;
      g.mesh.position.x = g.leapTo.x + (g.leapFrom.x - g.leapTo.x) * u;
      g.mesh.position.z = g.leapTo.z + (g.leapFrom.z - g.leapTo.z) * u;
      g.mesh.position.y = 4 * u * (1 - u) * (LEAP_HEIGHT * 0.75);
      g.mesh.rotation.y += (g.sleepRot - g.mesh.rotation.y) * 0.1;
      const dim = 1 - u;
      g.mesh.userData.eyeMatL.emissiveIntensity = 1.2 * dim;
      g.mesh.userData.eyeMatR.emissiveIntensity = 1.2 * dim;
      g.mesh.userData.bodyMat.emissiveIntensity = 0.3 + 0.7 * dim;
      g.mesh.scale.setScalar(0.9 + dim * 0.3);

      g.stateTime -= dt;
      if (g.stateTime <= 0) {
        g.state = 'sleeping';
        g.mesh.position.copy(g.sleepLocal);
        g.mesh.rotation.y = g.sleepRot;
        g.mesh.scale.setScalar(0.9);
        _deactivateGuardianCollider(g);
      }
      anyAwake = true;
    }

    // Keep the live collider glued to the guardian's world position
    // while it exists.
    if (g.collider) {
      g.mesh.getWorldPosition(g.collider.pos);
    }
  }

  // One-directional wall — only block from the outside.
  //
  // If the player is already past the wall (inside the cave, toward the
  // pedestal), the guardian colliders are temporarily spliced out of
  // the global list so they can't trap the player inside. They're
  // pushed back in as soon as the player is on the outside again.
  const playerInside = _playerLocal.x < INTERCEPT_RADIAL;
  for (const g of guardians) {
    if (!g.collider) continue;
    const inList = colliders.indexOf(g.collider) !== -1;
    if (playerInside && inList) {
      colliders.splice(colliders.indexOf(g.collider), 1);
    } else if (!playerInside && !inList) {
      colliders.push(g.collider);
    }
  }

  // Chip is only collectable when all guardians are fully asleep.
  if (chipEntry) chipEntry.locked = anyAwake;
}

// Exposed for the pickup-collection event — clears the local reference so
// post-collection updates don't try to toggle a removed entry.
events.on('pickup:collected', (entry) => {
  if (chipEntry && entry === chipEntry) chipEntry = null;
});
